import * as THREE from 'three';
import { ATLAS_SIZE, TEXTURE_SIZE } from '../constants';

type RGBA = [number, number, number, number];

function drawPixelBlock(
  data: Uint8ClampedArray,
  tileX: number, tileY: number,
  width: number,
  pixelFn: (x: number, y: number) => RGBA,
): void {
  const startX = tileX * TEXTURE_SIZE;
  const startY = tileY * TEXTURE_SIZE;
  for (let y = 0; y < TEXTURE_SIZE; y++) {
    for (let x = 0; x < TEXTURE_SIZE; x++) {
      const [r, g, b, a] = pixelFn(x, y);
      const idx = ((startY + y) * width + (startX + x)) * 4;
      data[idx] = r;
      data[idx + 1] = g;
      data[idx + 2] = b;
      data[idx + 3] = a;
    }
  }
}

function drawFromPixelData(
  data: Uint8ClampedArray,
  tileX: number, tileY: number,
  width: number,
  pixels: number[][],
  palette: Record<number, RGBA>,
): void {
  const startX = tileX * TEXTURE_SIZE;
  const startY = tileY * TEXTURE_SIZE;
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const colorIdx = pixels[y]?.[x] ?? 0;
      const [r, g, b, a] = palette[colorIdx] ?? [255, 0, 255, 255];
      const idx = ((startY + y) * width + (startX + x)) * 4;
      data[idx] = r;
      data[idx + 1] = g;
      data[idx + 2] = b;
      data[idx + 3] = a;
    }
  }
}

// Authentic Minecraft-style pixel art textures defined as 16x16 palette-indexed grids
// Each number maps to a color in the palette

const GRASS_TOP_PALETTE: Record<number, RGBA> = {
  0: [89, 168, 47, 255],
  1: [106, 176, 56, 255],
  2: [77, 153, 39, 255],
  3: [115, 185, 65, 255],
  4: [68, 142, 33, 255],
  5: [96, 172, 51, 255],
};
const GRASS_TOP: number[][] = [
  [0,1,2,0,3,1,0,2,1,3,0,2,1,0,3,2],
  [2,0,1,3,0,2,1,0,3,2,1,0,2,3,0,1],
  [1,3,0,2,1,0,3,2,0,1,3,1,0,2,1,3],
  [0,2,3,1,0,3,2,1,0,2,0,3,1,0,2,0],
  [3,0,1,0,2,1,0,3,1,0,2,1,3,1,0,2],
  [1,2,0,3,1,0,2,0,3,1,0,2,0,3,1,0],
  [0,1,3,2,0,3,1,2,0,1,3,0,2,1,0,3],
  [2,0,1,0,3,2,0,1,2,3,0,1,0,2,3,1],
  [0,3,2,1,0,1,3,0,1,2,1,3,0,1,2,0],
  [1,0,0,3,2,0,1,3,0,0,2,0,3,0,1,2],
  [3,2,1,0,1,3,0,2,3,1,0,2,1,3,0,1],
  [0,1,3,2,0,2,1,0,1,3,2,0,1,0,2,3],
  [2,0,0,1,3,0,2,1,0,2,1,3,2,1,0,0],
  [1,3,2,0,1,0,3,0,2,0,3,1,0,2,3,1],
  [0,2,1,3,0,2,0,3,1,0,2,0,1,3,0,2],
  [3,0,0,2,1,3,1,2,0,3,0,2,3,0,1,0],
];

const DIRT_PALETTE: Record<number, RGBA> = {
  0: [134, 96, 67, 255],
  1: [121, 85, 58, 255],
  2: [149, 108, 76, 255],
  3: [111, 78, 52, 255],
  4: [142, 101, 71, 255],
  5: [127, 90, 62, 255],
};
const DIRT: number[][] = [
  [0,1,2,0,3,0,1,2,0,3,1,0,2,0,1,3],
  [3,0,1,4,0,2,3,0,1,0,2,3,0,1,0,2],
  [1,2,0,3,1,0,2,1,3,0,1,2,3,0,2,0],
  [0,3,1,0,2,3,0,1,0,2,0,1,0,3,1,2],
  [2,0,3,1,0,1,3,2,0,3,1,0,2,1,0,3],
  [1,0,2,0,3,0,1,0,2,1,3,0,1,0,3,0],
  [0,3,1,2,0,2,0,3,1,0,2,3,0,2,1,0],
  [3,1,0,3,1,0,2,1,0,3,0,1,3,0,0,2],
  [0,2,1,0,2,3,1,0,3,2,1,0,2,1,3,1],
  [1,0,3,1,0,1,0,2,1,0,3,2,0,3,0,0],
  [2,3,0,2,3,0,3,1,0,1,0,1,3,0,2,3],
  [0,1,2,0,1,2,0,0,3,2,1,0,1,2,0,1],
  [3,0,1,3,0,1,2,3,0,0,2,3,0,1,3,0],
  [1,2,0,1,2,3,1,0,1,3,0,2,1,0,1,2],
  [0,3,2,0,1,0,3,2,0,1,3,0,2,3,0,1],
  [2,0,1,3,0,2,0,1,3,0,2,1,0,1,2,3],
];

const GRASS_SIDE_PALETTE: Record<number, RGBA> = {
  0: [89, 168, 47, 255],   // grass green top
  1: [106, 176, 56, 255],  // lighter grass
  2: [77, 153, 39, 255],   // darker grass
  3: [134, 96, 67, 255],   // dirt
  4: [121, 85, 58, 255],   // dark dirt
  5: [149, 108, 76, 255],  // light dirt
  6: [111, 78, 52, 255],   // darkest dirt
  7: [68, 142, 33, 255],   // grass edge
};
const GRASS_SIDE: number[][] = [
  [0,1,2,0,1,0,2,1,0,2,0,1,2,0,1,0],
  [7,0,1,7,0,2,0,7,1,0,7,0,1,7,0,2],
  [3,7,7,3,7,3,7,3,7,7,3,7,3,7,7,3],
  [4,3,5,3,4,3,5,3,3,4,3,5,3,4,3,5],
  [3,4,3,6,3,5,3,4,6,3,5,3,4,3,6,3],
  [5,3,6,3,4,3,6,3,5,3,3,4,3,5,3,4],
  [3,5,3,4,3,6,3,5,3,4,6,3,5,3,4,3],
  [4,3,5,3,6,3,4,3,6,3,3,5,3,4,3,6],
  [3,6,3,5,3,4,3,6,3,5,4,3,6,3,5,3],
  [5,3,4,3,3,5,6,3,4,3,3,6,3,5,3,4],
  [3,4,6,3,5,3,3,4,3,6,5,3,4,3,6,3],
  [6,3,3,4,3,6,5,3,5,3,3,4,3,6,3,5],
  [3,5,4,3,6,3,3,5,3,4,6,3,5,3,4,3],
  [4,3,3,6,3,5,4,3,6,3,3,5,3,4,3,6],
  [3,6,5,3,4,3,3,6,3,5,4,3,6,3,5,3],
  [5,3,3,4,3,6,5,3,4,3,3,6,3,5,3,4],
];

const STONE_PALETTE: Record<number, RGBA> = {
  0: [125, 125, 125, 255],
  1: [136, 136, 136, 255],
  2: [115, 115, 115, 255],
  3: [145, 145, 145, 255],
  4: [105, 105, 105, 255],
  5: [95, 95, 95, 255],   // cracks
  6: [155, 155, 155, 255],
};
const STONE: number[][] = [
  [0,1,0,2,0,1,3,0,2,0,1,0,3,0,2,1],
  [2,0,3,0,1,0,2,0,1,3,0,2,0,1,0,0],
  [0,2,0,1,5,5,0,1,0,0,2,0,1,0,3,2],
  [1,0,3,5,0,1,5,0,2,1,0,3,0,2,0,0],
  [0,1,0,2,0,0,1,0,0,5,5,0,1,0,2,1],
  [3,0,2,0,1,2,0,3,5,0,1,5,0,3,0,0],
  [0,2,0,1,0,0,2,0,0,1,0,0,5,0,1,2],
  [1,0,3,0,5,5,0,1,2,0,3,5,0,2,0,0],
  [0,1,0,5,0,1,3,0,0,2,0,0,1,0,3,1],
  [2,0,1,0,3,0,0,2,1,0,5,5,0,1,0,0],
  [0,3,0,2,0,1,2,0,0,5,0,1,5,0,2,3],
  [5,5,1,0,2,0,0,1,5,0,1,0,0,2,0,0],
  [1,0,5,0,0,3,1,0,0,2,0,3,0,0,1,2],
  [0,2,0,1,3,0,0,2,1,0,0,0,2,5,5,0],
  [1,0,3,0,0,1,2,0,0,3,1,5,0,0,1,0],
  [0,1,0,2,1,0,0,1,3,0,5,0,1,2,0,1],
];

const COBBLE_PALETTE: Record<number, RGBA> = {
  0: [122, 122, 122, 255],
  1: [135, 135, 135, 255],
  2: [108, 108, 108, 255],
  3: [145, 145, 145, 255],
  4: [90, 90, 90, 255],    // deep cracks
  5: [155, 155, 155, 255],
};
const COBBLE: number[][] = [
  [1,1,3,4,0,0,2,4,1,3,3,4,0,0,1,1],
  [0,1,1,4,0,2,2,4,3,1,1,4,2,0,0,1],
  [0,0,3,4,2,2,0,4,3,3,1,4,2,2,0,0],
  [4,4,4,4,4,4,4,4,4,4,4,4,4,4,4,4],
  [2,0,0,4,1,1,3,4,0,0,2,4,1,3,1,0],
  [2,2,0,4,1,0,3,4,0,2,2,4,1,3,3,0],
  [0,2,2,4,3,0,1,4,2,2,0,4,3,1,3,2],
  [4,4,4,4,4,4,4,4,4,4,4,4,4,4,4,4],
  [1,3,1,4,2,0,0,4,1,1,3,4,0,2,0,1],
  [1,1,3,4,2,2,0,4,3,1,1,4,0,2,2,1],
  [3,1,3,4,0,2,2,4,3,3,1,4,0,0,2,3],
  [4,4,4,4,4,4,4,4,4,4,4,4,4,4,4,4],
  [0,2,0,4,1,3,1,4,2,0,0,4,3,1,1,0],
  [0,0,2,4,1,1,3,4,2,0,2,4,3,3,1,0],
  [2,0,2,4,3,1,3,4,0,2,2,4,1,3,3,2],
  [4,4,4,4,4,4,4,4,4,4,4,4,4,4,4,4],
];

const SAND_PALETTE: Record<number, RGBA> = {
  0: [219, 207, 163, 255],
  1: [228, 216, 174, 255],
  2: [210, 198, 154, 255],
  3: [237, 226, 183, 255],
  4: [201, 189, 145, 255],
};

const WOOD_LOG_PALETTE: Record<number, RGBA> = {
  0: [103, 82, 49, 255],
  1: [114, 91, 55, 255],
  2: [92, 73, 43, 255],
  3: [125, 100, 62, 255],
  4: [82, 65, 37, 255],
};

const WOOD_TOP_PALETTE: Record<number, RGBA> = {
  0: [157, 131, 78, 255],
  1: [168, 142, 89, 255],
  2: [146, 120, 68, 255],
  3: [103, 82, 49, 255],  // bark ring
  4: [135, 110, 65, 255],
  5: [178, 152, 99, 255], // center
};

const PLANKS_PALETTE: Record<number, RGBA> = {
  0: [188, 152, 98, 255],
  1: [175, 140, 88, 255],
  2: [200, 164, 110, 255],
  3: [162, 130, 78, 255],
  4: [145, 118, 72, 255], // seam line
};

const LEAVES_PALETTE: Record<number, RGBA> = {
  0: [58, 120, 22, 220],
  1: [68, 135, 28, 220],
  2: [48, 105, 16, 220],
  3: [78, 145, 38, 220],
  4: [0, 0, 0, 0],       // transparent holes
};

function noise(x: number, y: number, seed: number): number {
  const n = Math.sin(x * 12.9898 + y * 78.233 + seed) * 43758.5453;
  return n - Math.floor(n);
}

function genTile(seed: number, palette: Record<number, RGBA>, count: number): number[][] {
  const grid: number[][] = [];
  for (let y = 0; y < 16; y++) {
    const row: number[] = [];
    for (let x = 0; x < 16; x++) {
      row.push(Math.floor(noise(x, y, seed) * count));
    }
    grid.push(row);
  }
  return grid;
}

function genWoodTop(): number[][] {
  const grid: number[][] = [];
  for (let y = 0; y < 16; y++) {
    const row: number[] = [];
    for (let x = 0; x < 16; x++) {
      const cx = x - 7.5, cy = y - 7.5;
      const dist = Math.sqrt(cx * cx + cy * cy);
      if (dist > 6.5) row.push(3);       // bark
      else if (dist > 5) row.push(0);
      else {
        const ring = Math.floor(Math.sin(dist * 1.8) * 2 + 2);
        row.push(Math.min(ring, 5));
      }
    }
    grid.push(row);
  }
  return grid;
}

function genPlanks(): number[][] {
  const grid: number[][] = [];
  for (let y = 0; y < 16; y++) {
    const row: number[] = [];
    const plankRow = Math.floor(y / 4);
    const offset = (plankRow % 2) * 5;
    for (let x = 0; x < 16; x++) {
      if (y % 4 === 0) { row.push(4); continue; }
      if ((x + offset) % 10 === 0) { row.push(4); continue; }
      const v = noise(x, y, 42);
      row.push(v > 0.6 ? 2 : v < 0.3 ? 1 : 0);
    }
    grid.push(row);
  }
  return grid;
}

function genLeaves(seed: number): number[][] {
  const grid: number[][] = [];
  for (let y = 0; y < 16; y++) {
    const row: number[] = [];
    for (let x = 0; x < 16; x++) {
      const v = noise(x, y, seed);
      if (v > 0.82) row.push(4);         // hole
      else if (v > 0.5) row.push(0);
      else if (v > 0.3) row.push(1);
      else if (v > 0.15) row.push(2);
      else row.push(3);
    }
    grid.push(row);
  }
  return grid;
}

function genOre(oreColor: RGBA, seed: number): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const sv = noise(x, y, 1);
    const stoneR = 125 + sv * 20 - 10;
    const stoneG = stoneR;
    const stoneB = stoneR;
    // Ore spots
    const ov = noise(x * 3.7 + 0.5, y * 3.7 + 0.5, seed);
    const ov2 = noise(x * 2.1, y * 2.1, seed + 50);
    if (ov > 0.62 && ov2 > 0.4) {
      return oreColor;
    }
    // Cracks
    if (noise(x * 1.5, y * 1.5, 99) > 0.88) {
      return [stoneR - 30, stoneG - 30, stoneB - 30, 255] as RGBA;
    }
    return [stoneR, stoneG, stoneB, 255] as RGBA;
  };
}

function genBrick(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const brickH = 4;
    const brickW = 8;
    const row = Math.floor(y / brickH);
    const offset = (row % 2) * 4;
    const bx = (x + offset) % brickW;
    const by = y % brickH;
    if (by === 0 || bx === 0) return [188, 185, 181, 255]; // mortar
    const v = noise(x, y, 70);
    return [179 + v * 25 - 12, 90 + v * 18 - 9, 68 + v * 14 - 7, 255];
  };
}

function genBedrock(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const v = noise(x, y, 80);
    const v2 = noise(x * 2, y * 2, 81);
    const base = 32 + v * 35 + v2 * 15;
    return [base, base, base, 255];
  };
}

function genWater(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const v = noise(x, y, 90);
    return [28 + v * 12, 56 + v * 20, 178 + v * 25, 190];
  };
}

function genGlass(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const border = x === 0 || x === 15 || y === 0 || y === 15;
    const innerBorder = x === 1 || x === 14 || y === 1 || y === 14;
    if (border) return [185, 210, 220, 230];
    if (innerBorder) return [200, 225, 235, 180];
    return [215, 237, 248, 50];
  };
}

function genTNTSide(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    if (y <= 1 || y >= 14) return [115, 110, 108, 255];
    if (y === 2 || y === 13) return [132, 126, 122, 255];
    // Red body
    const v = noise(x, y, 100);
    if (x >= 4 && x <= 11) {
      // TNT text area (white band)
      if (y >= 6 && y <= 9) return [220, 220, 210, 255];
    }
    return [188 + v * 30, 40 + v * 15, 30 + v * 12, 255];
  };
}

function genTNTTop(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const cx = x - 7.5, cy = y - 7.5;
    const dist = Math.sqrt(cx * cx + cy * cy);
    if (dist < 2.5) return [55, 50, 48, 255]; // fuse
    const v = noise(x, y, 101);
    return [188 + v * 25, 40 + v * 12, 30 + v * 10, 255];
  };
}

function genBookshelf(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    if (y <= 1 || y >= 14) {
      const v = noise(x, y, 42);
      return [188 + (v > 0.5 ? 12 : 0), 152 + (v > 0.5 ? 12 : 0), 98, 255]; // plank
    }
    // Shelf lines
    if (y === 7) return [95, 70, 42, 255];
    // Books
    const bookWidth = 3;
    const bookIdx = Math.floor(x / bookWidth);
    const bookEdge = x % bookWidth === 0;
    if (bookEdge) return [65, 48, 32, 255];
    const colors: RGBA[] = [
      [160, 48, 48, 255], [48, 80, 160, 255], [48, 130, 65, 255],
      [160, 130, 48, 255], [110, 48, 130, 255], [130, 60, 48, 255],
    ];
    return colors[bookIdx % colors.length];
  };
}

function genSnow(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const v = noise(x, y, 110);
    const base = 240 + v * 15;
    return [base, Math.min(255, base + 2), Math.min(255, base + 5), 255];
  };
}

function genIce(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const v = noise(x, y, 115);
    const crack = noise(x * 3, y * 3, 116) > 0.9;
    if (crack) return [140, 180, 220, 220];
    return [158 + v * 28, 200 + v * 18, 240 + v * 12, 210];
  };
}

function genClay(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const v = noise(x, y, 120);
    return [158 + v * 14, 162 + v * 14, 172 + v * 10, 255];
  };
}

function genSandstoneTop(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const v = noise(x, y, 125);
    return [218 + v * 16, 206 + v * 14, 163 + v * 12, 255];
  };
}

function genSandstoneSide(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const stripe = (y < 3 || y > 12) ? 10 : 0;
    const v = noise(x, y, 126);
    return [208 + v * 14 + stripe, 196 + v * 12 + stripe, 153 + v * 10, 255];
  };
}

function genSandstoneBottom(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const v = noise(x, y, 127);
    return [198 + v * 12, 186 + v * 10, 143 + v * 8, 255];
  };
}

function genCactusTop(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const cx = x - 7.5, cy = y - 7.5;
    const dist = Math.sqrt(cx * cx + cy * cy);
    if (dist > 6) return [0, 0, 0, 0];
    const v = noise(x, y, 130);
    if (dist < 2) return [35 + v * 15, 105 + v * 25, 25 + v * 10, 255];
    return [42 + v * 18, 130 + v * 30, 30 + v * 12, 255];
  };
}

function genCactusSide(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    if (x <= 1 || x >= 14) return [30, 95, 20, 255]; // dark edge
    const v = noise(x, y, 131);
    // Spines
    if ((x === 4 || x === 11) && y % 4 === 2) return [180, 190, 140, 255];
    const stripe = Math.sin(y * 0.4) * 6;
    return [38 + v * 15 + stripe, 118 + v * 28 + stripe, 28 + v * 10, 255];
  };
}

function genCraftingTop(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    // Grid pattern
    if (x === 4 || x === 11 || y === 4 || y === 11) return [82, 62, 38, 255];
    const v = noise(x, y, 140);
    return [188 + v * 14, 152 + v * 12, 98 + v * 8, 255];
  };
}

function genCraftingFront(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    // Saw/tool pattern at top
    if (y < 4) {
      if (x >= 3 && x <= 5 && y >= 1) return [120, 120, 120, 255]; // tool
      if (x >= 10 && x <= 12 && y >= 1) return [120, 120, 120, 255];
    }
    const v = noise(x, y, 141);
    return [188 + v * 14, 152 + v * 12, 98 + v * 8, 255];
  };
}

function genFurnaceTop(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const v = noise(x, y, 150);
    return [118 + v * 16, 118 + v * 16, 118 + v * 16, 255];
  };
}

function genFurnaceFront(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    // Furnace opening
    if (x >= 4 && x <= 11 && y >= 5 && y <= 12) {
      if (x === 4 || x === 11 || y === 5 || y === 12) return [50, 50, 50, 255];
      return [25, 25, 25, 255]; // dark inside
    }
    const v = noise(x, y, 151);
    return [118 + v * 16, 118 + v * 16, 118 + v * 16, 255];
  };
}

function genFurnaceSide(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const v = noise(x, y, 152);
    return [118 + v * 16, 118 + v * 16, 118 + v * 16, 255];
  };
}

function genTorch(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    // Only the stick part, transparent elsewhere
    if (x < 6 || x > 9) return [0, 0, 0, 0];
    if (y <= 1) return [255, 210, 80, 255];  // flame tip
    if (y <= 3) return [255, 170, 40, 255];  // flame
    if (y <= 5) return [240, 130, 20, 255];  // flame base
    // Stick
    if (x < 7 || x > 8) return [0, 0, 0, 0];
    const v = noise(x, y, 160);
    return [135 + v * 18, 105 + v * 14, 60 + v * 10, 255];
  };
}

function genBirchLog(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const v = noise(x, y, 170);
    const base = 218 + v * 22;
    // Dark birch spots
    const dark = noise(x * 1.8, y * 0.6, 171) > 0.78;
    if (dark) return [55, 48, 38, 255];
    return [base, base - 4, base - 14, 255];
  };
}

function genBirchLeaves(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const v = noise(x, y, 175);
    if (v > 0.83) return [0, 0, 0, 0];
    return [62 + v * 28, 148 + v * 35, 42 + v * 18, 210];
  };
}

function genTallGrass(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    // Thin blades
    const blade1 = Math.abs(x - 4 - Math.sin(y * 0.8) * 2) < 0.8;
    const blade2 = Math.abs(x - 8 + Math.sin(y * 0.6) * 1.5) < 0.8;
    const blade3 = Math.abs(x - 12 - Math.cos(y * 0.7) * 1.8) < 0.8;
    if (y < 2) return [0, 0, 0, 0]; // top empty
    if (blade1 || blade2 || blade3) {
      const v = noise(x, y, 180);
      return [65 + v * 22, 145 + v * 35, 32 + v * 15, 230];
    }
    return [0, 0, 0, 0];
  };
}

function genFlower(petalR: number, petalG: number, petalB: number): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    // Stem
    if ((x === 7 || x === 8) && y >= 9) {
      return [42, 125, 32, 255];
    }
    // Petals
    const cx = x - 7.5, cy = y - 5;
    const dist = Math.sqrt(cx * cx + cy * cy);
    if (dist < 1.5) return [255, 235, 55, 255]; // center
    if (dist < 3.5) return [petalR, petalG, petalB, 255];
    // Leaf
    if (y === 11 && (x === 5 || x === 6 || x === 9 || x === 10)) {
      return [42, 125, 32, 255];
    }
    return [0, 0, 0, 0];
  };
}

function genGravel(): (x: number, y: number) => RGBA {
  return (x: number, y: number) => {
    const v = noise(x, y, 200);
    const v2 = noise(x * 2.5, y * 2.5, 201);
    const base = 125 + v * 30 + v2 * 15;
    const rOff = v2 > 0.6 ? 8 : 0;
    return [base + rOff, base - 2, base - 6, 255];
  };
}

export function generateTextureAtlas(): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = ATLAS_SIZE;
  canvas.height = ATLAS_SIZE;
  const ctx = canvas.getContext('2d')!;
  const imageData = ctx.createImageData(ATLAS_SIZE, ATLAS_SIZE);
  const data = imageData.data;
  data.fill(0);

  const tile = (col: number, row: number, fn: (x: number, y: number) => RGBA) => {
    drawPixelBlock(data, col, row, ATLAS_SIZE, fn);
  };
  const tileP = (col: number, row: number, pixels: number[][], palette: Record<number, RGBA>) => {
    drawFromPixelData(data, col, row, ATLAS_SIZE, pixels, palette);
  };

  // Row 0: grass_top(0), grass_side(1), dirt(2), stone(3), cobblestone(4), sand(5), gravel(6)
  tileP(0, 0, GRASS_TOP, GRASS_TOP_PALETTE);
  tileP(1, 0, GRASS_SIDE, GRASS_SIDE_PALETTE);
  tileP(2, 0, DIRT, DIRT_PALETTE);
  tileP(3, 0, STONE, STONE_PALETTE);
  tileP(4, 0, COBBLE, COBBLE_PALETTE);
  tileP(5, 0, genTile(50, SAND_PALETTE, 5), SAND_PALETTE);
  tile(6, 0, genGravel());

  // Row 1: oak_log_top(32), oak_log_side(33), oak_leaves(34), oak_planks(35), bedrock(36), water(37), glass(38)
  tileP(0, 1, genWoodTop(), WOOD_TOP_PALETTE);
  tileP(1, 1, genTile(60, WOOD_LOG_PALETTE, 5), WOOD_LOG_PALETTE);
  tileP(2, 1, genLeaves(70), LEAVES_PALETTE);
  tileP(3, 1, genPlanks(), PLANKS_PALETTE);
  tile(4, 1, genBedrock());
  tile(5, 1, genWater());
  tile(6, 1, genGlass());

  // Row 2: coal_ore(64), iron_ore(65), gold_ore(66), diamond_ore(67), brick(68), tnt_top(69), tnt_side(70), tnt_bottom(71)
  tile(0, 2, genOre([45, 45, 45, 255], 210));
  tile(1, 2, genOre([196, 178, 160, 255], 220));
  tile(2, 2, genOre([252, 212, 55, 255], 230));
  tile(3, 2, genOre([75, 218, 238, 255], 240));
  tile(4, 2, genBrick());
  tile(5, 2, genTNTTop());
  tile(6, 2, genTNTSide());
  tile(7, 2, genFurnaceTop()); // tnt_bottom reuses furnace top (gray)

  // Row 3: bookshelf(96), snow(97), ice(98), clay(99), sandstone_top(100), sandstone_side(101), sandstone_bottom(102)
  tile(0, 3, genBookshelf());
  tile(1, 3, genSnow());
  tile(2, 3, genIce());
  tile(3, 3, genClay());
  tile(4, 3, genSandstoneTop());
  tile(5, 3, genSandstoneSide());
  tile(6, 3, genSandstoneBottom());

  // Row 4: cactus_top(128), cactus_side(129), cactus_bottom(130), crafting_table_top(131), crafting_table_front(132), crafting_table_side(133)
  tile(0, 4, genCactusTop());
  tile(1, 4, genCactusSide());
  tile(2, 4, genSandstoneSide()); // cactus bottom
  tile(3, 4, genCraftingTop());
  tile(4, 4, genCraftingFront());
  tile(5, 4, genCraftingFront()); // side similar to front

  // Row 5: furnace_top(160), furnace_front(161), furnace_side(162), torch(163), birch_log_side(164), birch_leaves(165)
  tile(0, 5, genFurnaceTop());
  tile(1, 5, genFurnaceFront());
  tile(2, 5, genFurnaceSide());
  tile(3, 5, genTorch());
  tile(4, 5, genBirchLog());
  tile(5, 5, genBirchLeaves());

  // Row 6: tall_grass(192), flower_red(193), flower_yellow(194)
  tile(0, 6, genTallGrass());
  tile(1, 6, genFlower(218, 45, 45));
  tile(2, 6, genFlower(255, 230, 55));

  ctx.putImageData(imageData, 0, 0);

  const texture = new THREE.CanvasTexture(canvas);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.colorSpace = THREE.SRGBColorSpace;

  return texture;
}
